import React, { useState, useEffect } from 'react';
import { Wrench, Lock, History } from 'lucide-react';
import { api } from '../../lib/api';
import { Switch } from '../../components/ui/switch';
import ImageField from '../../components/ImageField';
import { requestPin } from '../../components/PinModal';
import { toast } from 'sonner';

const MODES = [
  { id: 'operativa', label: 'Web operativa', desc: 'La tienda es visible para todos los visitantes.', color: 'border-teal-400 bg-teal-50' },
  { id: 'mantenimiento', label: 'Modo mantenimiento', desc: 'Todos los visitantes ven la página de mantenimiento. MATRIZ mantiene acceso por /admin.', color: 'border-amber-400 bg-amber-50' },
  { id: 'proximamente', label: 'Próximamente', desc: 'Página de lanzamiento previa a una apertura.', color: 'border-sky-400 bg-sky-50' },
];

export default function AdminMantenimiento() {
  const [config, setConfig] = useState(null);
  const [log, setLog] = useState([]);
  const [targetMode, setTargetMode] = useState(null);
  const [admState, setAdmState] = useState('abiertas');

  useEffect(() => {
    api.getMaintenance().then(setConfig).catch(() => {});
    api.getMaintenanceLog().then(setLog).catch(() => {});
    api.getScmeSettings().then((s) => setAdmState(s.admissionsState || 'abiertas')).catch(() => {});
  }, []);

  const applyMode = async () => {
    const token = await requestPin(`Cambiar estado de la web a "${MODES.find((m) => m.id === targetMode)?.label}"`);
    if (!token) return;
    try {
      const updated = await api.updateMaintenance({ mode: targetMode }, token);
      setConfig(updated);
      setTargetMode(null);
      api.getMaintenanceLog().then(setLog).catch(() => {});
      toast.success(`Estado cambiado a: ${MODES.find((m) => m.id === updated.mode)?.label}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cambiar el estado');
    }
  };

  const saveContent = async () => {
    try {
      const { id, mode, ...data } = config;
      const updated = await api.updateMaintenance(data);
      setConfig(updated);
      toast.success('Página de mantenimiento guardada');
    } catch { toast.error('Error al guardar'); }
  };

  const toggleAdmissions = async (value) => {
    try {
      const updated = await api.updateScmeSettings({ admissionsState: value });
      setAdmState(updated.admissionsState);
      toast.success(value === 'abiertas' ? 'Admisiones abiertas' : 'Admisiones pausadas');
    } catch { toast.error('Error al actualizar admisiones'); }
  };

  if (!config) return null;

  return (
    <div data-testid="admin-mantenimiento">
      <div className="flex items-center gap-2 mb-1">
        <Wrench className="w-6 h-6 text-amber-500" />
        <h1 className="text-2xl font-black text-stone-800">Mantenimiento</h1>
      </div>
      <p className="text-sm text-stone-500 mb-6">Activa o desactiva la disponibilidad pública de la web en segundos. Protegido con PIN Maestro.</p>

      {/* Estado de la web */}
      <div className="bg-white rounded-lg border border-stone-200 p-6 max-w-3xl mb-6" data-testid="maintenance-mode-panel">
        <h2 className="font-black text-stone-800 mb-4">Estado de la web</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          {MODES.map((m) => (
            <button
              key={m.id}
              onClick={() => setTargetMode(m.id === config.mode ? null : m.id)}
              className={`text-left border-2 rounded-lg p-4 transition-colors ${config.mode === m.id ? m.color : targetMode === m.id ? 'border-stone-800 bg-stone-50' : 'border-stone-200 bg-white hover:border-stone-400'}`}
              data-testid={`mode-${m.id}`}
            >
              <p className="font-bold text-sm text-stone-800">{m.label} {config.mode === m.id && <span className="text-[10px] font-black uppercase text-teal-600 ml-1">Activo</span>}</p>
              <p className="text-xs text-stone-500 mt-1">{m.desc}</p>
            </button>
          ))}
        </div>
        {targetMode && targetMode !== config.mode && (
          <div className="flex items-center gap-3 border-t border-stone-100 pt-4" data-testid="mode-confirm">
            <Lock className="w-4 h-4 text-stone-400" />
            <button onClick={applyMode} className="btn-natur px-5 py-2.5 rounded text-sm font-bold" data-testid="maintenance-apply">
              Cambiar a "{MODES.find((m) => m.id === targetMode)?.label}"
            </button>
          </div>
        )}
      </div>

      {/* Admisiones */}
      <div className="bg-white rounded-lg border border-stone-200 p-6 max-w-3xl mb-6" data-testid="maintenance-admissions-panel">
        <h2 className="font-black text-stone-800 mb-1">Admisiones</h2>
        <p className="text-xs text-stone-500 mb-4">Acceso rápido al estado global de admisiones (también configurable en Admisiones).</p>
        <div className="flex gap-2">
          {[['abiertas', 'Admisiones abiertas'], ['pausadas', 'Admisiones pausadas']].map(([v, l]) => (
            <button key={v} onClick={() => toggleAdmissions(v)} className={`px-5 py-2.5 rounded-full text-sm font-bold border transition-colors ${admState === v ? 'bg-stone-800 text-white border-stone-800' : 'bg-white text-stone-600 border-stone-300 hover:border-stone-500'}`} data-testid={`admissions-${v}`}>
              {l}
            </button>
          ))}
          {admState === 'espera' && <span className="text-xs text-sky-600 font-bold self-center">Actualmente: Lista de espera activa</span>}
        </div>
      </div>

      {/* Contenido de la página */}
      <div className="bg-white rounded-lg border border-stone-200 p-6 max-w-3xl mb-6 space-y-4" data-testid="maintenance-content-panel">
        <h2 className="font-black text-stone-800">Página de mantenimiento</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="block text-xs font-bold text-stone-500 mb-1">Título</label>
            <input value={config.title} onChange={(e) => setConfig({ ...config, title: e.target.value })} className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="maintenance-title-input" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-bold text-stone-500 mb-1">Subtítulo</label>
            <textarea value={config.subtitle} onChange={(e) => setConfig({ ...config, subtitle: e.target.value })} rows={3} className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="maintenance-subtitle-input" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-bold text-stone-500 mb-1">Texto adicional (opcional)</label>
            <textarea value={config.extraText} onChange={(e) => setConfig({ ...config, extraText: e.target.value })} rows={2} className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="maintenance-extra-input" />
          </div>
          <div>
            <label className="block text-xs font-bold text-stone-500 mb-1">Fecha estimada (opcional)</label>
            <input type="date" value={config.estimatedDate} onChange={(e) => setConfig({ ...config, estimatedDate: e.target.value })} className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="maintenance-date-input" />
          </div>
          <div className="flex items-center gap-2 pt-5">
            <Switch checked={!!config.showWhatsapp} onCheckedChange={(v) => setConfig({ ...config, showWhatsapp: v })} data-testid="maintenance-whatsapp-switch" />
            <span className="text-sm text-stone-700">Botón WhatsApp</span>
          </div>
          <ImageField label="Imagen de fondo (opcional)" value={config.backgroundImage || ''} onChange={(url) => setConfig({ ...config, backgroundImage: url })} testid="maintenance-bg-image" />
          <ImageField label="Vídeo de fondo (opcional)" value={config.backgroundVideo || ''} onChange={(url) => setConfig({ ...config, backgroundVideo: url })} type="video" testid="maintenance-bg-video" />
        </div>
        <div>
          <label className="block text-xs font-bold text-stone-500 mb-2">Redes sociales (opcionales)</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {['facebook', 'instagram', 'tiktok', 'youtube'].map((k) => (
              <input key={k} value={config.socials?.[k] || ''} onChange={(e) => setConfig({ ...config, socials: { ...config.socials, [k]: e.target.value } })} placeholder={`URL ${k.charAt(0).toUpperCase() + k.slice(1)}`} className="border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid={`maintenance-social-${k}`} />
            ))}
          </div>
        </div>
        <button onClick={saveContent} className="btn-natur px-6 py-2.5 rounded text-sm font-bold" data-testid="maintenance-save-content">Guardar página</button>
      </div>

      {/* Registro */}
      {log.length > 0 && (
        <div className="bg-white rounded-lg border border-stone-200 p-6 max-w-3xl" data-testid="maintenance-log">
          <h2 className="font-black text-stone-800 mb-3 flex items-center gap-2"><History className="w-4 h-4 text-stone-400" /> Registro de cambios</h2>
          <div className="space-y-1.5">
            {log.map((l) => (
              <p key={l.id} className="text-xs text-stone-500">
                <span className="font-bold text-stone-700">{l.user}</span> · {new Date(l.date).toLocaleString('es-ES')} · <span className="font-bold">{l.change}</span>
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
